/**
 * Default carousel section order for the homepage
 * This order is used when the Lab feature flag for carousel reordering is not enabled
 */
export type HomepageCarouselSectionIdentifier =
  App.LabCarouselSectionOrderPayload['sectionOrder'][number]

const TRENDING_DESTINATIONS_SECTION = 'trendingDestinations' satisfies HomepageCarouselSectionIdentifier
const HOMEPAGE_FLIGHT_DEALS_SECTION = 'homepageFlightDeals' satisfies HomepageCarouselSectionIdentifier

export const TRENDING_DESTINATIONS_SECTION_IDENTIFIER: HomepageCarouselSectionIdentifier = TRENDING_DESTINATIONS_SECTION
export const HOMEPAGE_FLIGHT_DEALS_SECTION_IDENTIFIER: HomepageCarouselSectionIdentifier = HOMEPAGE_FLIGHT_DEALS_SECTION

export type LoyaltyCreditCardHomepageBannerAnchorSection =
  | typeof HOMEPAGE_FLIGHT_DEALS_SECTION
  | typeof TRENDING_DESTINATIONS_SECTION

/**
 * Lab section-order payloads replace the defaults, so flight deals may be absent.
 * Prefer anchoring the Société credit-card banner under flight deals; fall back to
 * trending destinations so legacy Lab payloads still render the banner.
 */
export function resolveLoyaltyCreditCardHomepageBannerAnchor(
  sectionOrder: ReadonlyArray<HomepageCarouselSectionIdentifier>,
): LoyaltyCreditCardHomepageBannerAnchorSection | null {
  if (sectionOrder.includes(HOMEPAGE_FLIGHT_DEALS_SECTION)) {
    return HOMEPAGE_FLIGHT_DEALS_SECTION
  }
  if (sectionOrder.includes(TRENDING_DESTINATIONS_SECTION)) {
    return TRENDING_DESTINATIONS_SECTION
  }
  return null
}

// Canonical Lab section for the mutually exclusive personalised hotel slot
// (family, weekend, post-purchase). Existing payloads may still use the
// CRO-7339 identifier; resolveHomepageCarouselSectionOrder aliases it.
export const HOMEPAGE_SEARCH_SLOT_SECTION_IDENTIFIER: HomepageCarouselSectionIdentifier = 'personalisedHotelCarouselSlot'
export const LEGACY_HOMEPAGE_SEARCH_SLOT_SECTION_IDENTIFIER: HomepageCarouselSectionIdentifier = 'recentSearchHotelCarousel'

export function resolveHomepageCarouselSectionOrder(
  sectionOrder: ReadonlyArray<HomepageCarouselSectionIdentifier>,
): Array<HomepageCarouselSectionIdentifier> {
  const resolved: Array<HomepageCarouselSectionIdentifier> = []
  const seen = new Set<HomepageCarouselSectionIdentifier>()

  for (const identifier of sectionOrder) {
    const canonical = identifier === LEGACY_HOMEPAGE_SEARCH_SLOT_SECTION_IDENTIFIER
      ? HOMEPAGE_SEARCH_SLOT_SECTION_IDENTIFIER
      : identifier
    if (seen.has(canonical)) continue
    seen.add(canonical)
    resolved.push(canonical)
  }

  return resolved
}

// Maldives campaign priority for 8 October 2026. Only promote offers already eligible for the carousel.
export const MALDIVES_CAROUSEL_PRIORITISED_OFFER_IDS = [
  '006RF00000OjiFCYAZ', // Hard Rock
  '006RF00000aBtKfYAK', // Villa Nautica
  '006RF00000hKnyHYAS', // The Standard
]

export const DEFAULT_CAROUSEL_ORDER: Array<HomepageCarouselSectionIdentifier> = [
  'componentBannerGroup',
  'upsellSection',
  'frontPageUSP',
  'carHireBundledUpcomingOrder',
  TRENDING_DESTINATIONS_SECTION_IDENTIFIER,
  'maldivesCharterCarousel',
  HOMEPAGE_SEARCH_SLOT_SECTION_IDENTIFIER,
  'offerEndingSoonCarousel',
  'topPicksForMeCarousel',
  'homepageFlightCampaigns',
  HOMEPAGE_FLIGHT_DEALS_SECTION_IDENTIFIER,
  'societeStayUpgradeCarousel',
  'tourOfferCarousel',
  'supportTile',
  'cruiseOfferCarousel',
  'trustIndicatorSection',
  'experienceLuxPlusCarousel',
  'smartExperienceOfferCarousel',
  'marketingCarousel',
  'luxPlusOfferCarousel',
  'hotelOfferCarousel',
  'ultraLuxOfferCarousel',
  'merchandisePointsCarousel',
  'valuePropositionBanner',
  'blogCarousel',
  'browseOfferTypes',
]
